'use client';

import { Button, Form, FormSubmit, TextareaField, toast, useConfig, useLocale, useTranslation } from '@payloadcms/ui';
import { getRouteResolver } from '@websolutespa/payload-utils';
import { Data, FormState } from 'payload';
import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import {
  isChunkText, isChunkTextOrString, LlmApp, LlmChunkItem, LlmDecodedMessage, LlmMessage, LlmUpload,
  LlmUploaded, PythonStreamResponse
} from '../../api/types';
import { LLM_DEFAULT_MAX_FILE_SIZE, LLM_DEFAULT_MIME_TYPES } from '../../types';
import './Llm.scss';
import { LlmChatCompletion } from './LlmChatCompletion';
import { LlmThread } from './LlmThread';
import { fileToResizedBase64 } from './upload';

export const LlmTestApp_ = ({ app }: { app: LlmApp }) => {
  const { t } = useTranslation<any, any>();
  const { config } = useConfig();
  const { localization, routes, serverURL } = config;
  const routeResolver = getRouteResolver(config);
  const locale = useLocale();
  const [response, setResponse] = useState<PythonStreamResponse>();
  const [messages, setMessages] = useState<LlmDecodedMessage[]>([]);
  const [completion, setCompletion] = useState<LlmMessage[] | undefined>();
  const [upload, setUpload] = useState<LlmUpload | undefined>();
  const ref = useRef<HTMLDivElement>(null);

  const initialState = useMemo(() => {
    const initialState: FormState = {
      message: {
        initialValue: undefined,
        valid: true,
        value: undefined,
      },
    };
    return initialState;
  }, []);

  const getUserMessage = (prompt: string, uploaded?: LlmUploaded): LlmDecodedMessage => {
    if (uploaded) {
      const chunkItems = [{
        type: 'text' as const,
        text: prompt,
      }, {
        type: uploaded.type,
        url: uploaded.url,
      }] as LlmChunkItem[];
      const userMessage = {
        chunks: chunkItems,
        content: chunkItems,
        role: 'user',
      };
      return userMessage;
    } else {
      const userMessage = {
        chunks: [{
          type: 'text' as const,
          text: prompt,
        }],
        content: prompt,
        role: 'user',
      };
      return userMessage;
    }
  };

  const sendMessage = (prompt: string, uploaded?: LlmUploaded) => {
    const userMessage = getUserMessage(prompt, uploaded);
    // console.log('LlmTestApp.sendMessage', userMessage);
    const completion = [...messages, userMessage];
    setUpload(undefined);
    setMessages(completion);
    setCompletion(completion);
  };

  const getUploaded = async (upload?: LlmUpload | undefined): Promise<LlmUploaded | undefined> => {
    if (upload) {
      const url = routeResolver.api(`/llmUserUpload/upload?locale=${locale}`);
      try {
        const data = new FormData();
        data.set('appKey', app.settings.credentials.appKey);
        data.set('apiKey', app.settings.credentials.apiKey);
        data.set('file', upload.file, upload.file.name);
        const response = await fetch(url, {
          method: 'POST',
          body: data,
        });
        if (response.ok) {
          const uploaded = await response.json() as LlmUploaded | undefined;
          return uploaded && {
            ...uploaded,
            type: uploaded.mimeType.indexOf('image') === 0 ? 'image' : 'file',
          };
        } else {
          console.error('useLlm.api.fetch', response, url);
          return undefined;
        }
      } catch (error) {
        console.error('useLlm.api.fetch', error, url);
        return undefined;
      }
    }
    return undefined;
  };

  const onSubmit = async (fields: FormState, data: Data) => {
    if (!data.message) {
      return;
    }
    if (fields.message) {
      fields.message.value = '';
    }
    let uploaded: LlmUploaded | undefined;
    if (upload) {
      uploaded = await getUploaded(upload);
      if (!uploaded) {
        return;
      }
    }
    sendMessage(data.message, uploaded);
  };

  const onSuccess = async (json: unknown) => {
    return initialState;
  };

  const onError = (error: any) => {
    console.log('LlmTestApp.onError', error);
    toast.error(`An error occurred: ${error.message}.`);
  };

  const onMessage = (response: PythonStreamResponse) => {
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: 'smooth',
      });
      ref.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const onEnd = (response: PythonStreamResponse) => {
    // console.log('LlmTestApp.onEnd', response);
    const chunks = [...response.chunks];
    const assistantMessage = {
      chunks,
      content: '',
      role: 'assistant',
    };
    // filtering history
    const validChunks = chunks.filter(
      (x) =>
        ![
          'header',
          'info',
          'end',
          'formRequest',
          'formRecap',
          'formRecapSuccess',
          'formRecapError',
        ].includes(x.type)
    );
    const parsedChunks: LlmChunkItem[] = [];
    validChunks.reduce((p, c) => {
      const firstChunk = p.length > 1 && p[p.length - 2];
      const secondChunk = p.length > 1 && p[p.length - 1];
      if (
        firstChunk &&
        secondChunk &&
        isChunkTextOrString(c) &&
        isChunkTextOrString(firstChunk) &&
        isChunkTextOrString(secondChunk)
      ) {
        const text = (isChunkText(c) ? c.text : c.content) || '';
        if (isChunkText(secondChunk)) {
          secondChunk.text += text;
        } else {
          secondChunk.content += text;
        }
      } else {
        p.push({ ...c });
      }
      return p;
    }, parsedChunks);
    assistantMessage.content =
      parsedChunks.map((x) => JSON.stringify(x)).join(',') + ',';
    const responseMessages = [...messages, assistantMessage];
    setResponse(response);
    setMessages(responseMessages);
    setCompletion(undefined);
  };

  const hasUpload = app?.contents.enableUpload === true;

  const addFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target?.files;
    try {
      if (files && files.length > 0) {
        const file = files[0] as File;
        if (!file) {
          console.log('LlmTestApp.uploadFile.error', 'file not found');
          return;
        }
        const maxFileSize = app.contents.maxFileSize || LLM_DEFAULT_MAX_FILE_SIZE;
        if (file.size > maxFileSize) {
          alert(`Max file size ${maxFileSize}`);
          return;
        }
        const base64 = file.type.indexOf('image') === 0 ? await fileToResizedBase64(file) : undefined;
        (event.target.value as any) = null;
        const upload = {
          file,
          base64,
        };
        setUpload(upload);
        console.log('LlmTestApp.uploadFile.success', upload);
      } else {
        console.log('LlmTestApp.uploadFile.error', 'file not found');
      }
    } catch (error) {
      console.log('LlmTestApp.uploadFile.error', 'cannot parse file');
    }
  };

  const removeFile = () => {
    setUpload(undefined);
  };

  const systemMessage = app.settings.llmConfig.prompt.prompt?.systemMessage ||
    app.settings.llmConfig.prompt.systemMessage;

  const sampleInputTexts = app.contents.sampleInputTexts?.filter(x => x.sampleInputText);
  const layoutBuilder = app.contents.layoutBuilder?.filter(x => x.title && x.message);
  const mimeTypes = app.contents.mimeTypes || LLM_DEFAULT_MIME_TYPES;

  return (
    <Form
      initialState={initialState}
      disableSuccessStatus
      onSubmit={onSubmit}
      onSuccess={onSuccess}
    >
      <div className="llm__head">
        <h1>{app.name}</h1>
        {systemMessage && (
          <div className="llm__row">
            <h4>System Message</h4>
            <pre>{systemMessage}</pre>
          </div>
        )}
        {sampleInputTexts && sampleInputTexts.length > 0 && (
          <div className="llm__row llm__flex">
            {sampleInputTexts.map((x, i) => (
              <button
                key={`sampleInputTexts_${i}`}
                type="button"
                className="btn btn--style-secondary btn--size-small"
                onClick={() => sendMessage(x.sampleInputText)}
              >
                {x.sampleInputText}
              </button>
            ))}
          </div>
        )}
        {layoutBuilder && layoutBuilder.length > 0 && (
          <div className="llm__row llm__flex">
            {layoutBuilder.map((x, i) => (
              <button
                key={`sampleInputTexts_${i}`}
                type="button"
                className="btn btn--style-secondary btn--size-small"
                onClick={() => sendMessage(x.message)}
              >
                {x.title}
              </button>
            ))}
          </div>
        )}
      </div>
      <div ref={ref} className="llm__main">
        <LlmThread messages={messages} />
        {completion && (
          <LlmChatCompletion
            apiKey={app.settings.credentials.apiKey}
            appKey={app.settings.credentials.appKey}
            completion={completion}
            test={false}
            threadId={response?.threadId}
            onMessage={onMessage}
            onEnd={onEnd}
            onError={onError}
          />
        )}
      </div>
      <div className="llm__foot">
        <TextareaField
          field={{
            name: 'message',
            label: t('general:message'),
            required: true,
            admin: {
              placeholder: 'Type you message',
            } as any,
          }}
          path="message"
        />
        <FormSubmit type="submit" disabled={typeof completion !== 'undefined'}>
          {t('general:send')}
        </FormSubmit>
        {hasUpload && (
          <>
            <div className="llm__upload">
              <Button buttonStyle="secondary" className="llm__upload-cta">
                {t('general:upload')}
              </Button>
              <input className="llm__upload-input" type="file" id="file-input" accept={mimeTypes} onChange={addFile} />
            </div>
            {upload && (
              <Button buttonStyle="secondary" size="small" className="llm__upload-file" onClick={() => removeFile()}>
                <span className="llm__upload-filename">{upload.file.name}</span>
                <span>✖️</span>
                <img className="llm__upload-image" src={upload.base64} alt="" />
              </Button>
            )}
          </>
        )}
      </div>
    </Form>
  );
};

export const LlmTestApp = React.memo(LlmTestApp_);
